import { CircleHelp, Search } from 'lucide-react'
import { NavLink } from 'react-router'

import type { MyOrg, User } from '@/api/types'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { useShell } from '@/hooks/useShell'
import { HUES, type Hue } from '@/lib/hues'
import { NAV_ITEMS, SETTINGS_ITEM } from '@/lib/navigation'
import { prefetchSection } from '@/lib/pages'
import { SITE_URL } from '@/lib/site'
import { BrandLogo } from './BrandLogo'
import { OrgSwitcher } from './OrgSwitcher'
import { UserMenu } from './UserMenu'

/** A section in the sidebar; resting on it (or tabbing to it) starts loading the section's code. */
const NavItemLink = ({
  to,
  section,
  label,
  icon: Icon,
  hue,
}: {
  to: string
  /** The section's path (lib/navigation.ts), for prefetching its page. */
  section: string
  label: string
  icon: (typeof NAV_ITEMS)[number]['icon']
  /** The icon's hue; Settings has none and stays quiet. */
  hue?: Hue
}) => (
  <SidebarMenuItem>
    <NavLink to={to} onMouseEnter={() => void prefetchSection(section)} onFocus={() => void prefetchSection(section)}>
      {({ isActive }) => (
        <SidebarMenuButton asChild isActive={isActive}>
          <span>
            <Icon className={hue && HUES[hue].text} />
            <span>{label}</span>
          </span>
        </SidebarMenuButton>
      )}
    </NavLink>
  </SidebarMenuItem>
)

/**
 * The left sidebar (DESIGN.md): the logo and the org (switcher); every section this role can see, Settings last;
 * then Search (the command menu, Ctrl K) and Help side by side above the person.
 */
export const AppSidebar = ({ org, orgs, user }: { org: MyOrg; orgs: MyOrg[]; user: User }) => {
  const base = `/o/${org.slug}`
  const shell = useShell()
  return (
    <Sidebar className="print:hidden">
      <SidebarHeader>
        <div className="flex h-9 items-center px-2">
          <BrandLogo className="group-data-[collapsible=icon]:hidden" />
          <BrandLogo variant="mark" className="hidden size-5 group-data-[collapsible=icon]:block" />
        </div>
        <OrgSwitcher org={org} orgs={orgs} />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV_ITEMS.filter((item) => org.permissions.includes(item.permission)).map((item) => (
                <NavItemLink
                  key={item.path}
                  to={`${base}/${item.path}`}
                  section={item.path}
                  label={item.label}
                  icon={item.icon}
                  hue={item.hue}
                />
              ))}
              <NavItemLink
                to={`${base}/${SETTINGS_ITEM.path}`}
                section={SETTINGS_ITEM.path}
                label={SETTINGS_ITEM.label}
                icon={SETTINGS_ITEM.icon}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu className="flex-row gap-1 group-data-[collapsible=icon]:flex-col">
              {shell?.openSearch && (
                <SidebarMenuItem className="min-w-0 flex-1">
                  <SidebarMenuButton onClick={shell.openSearch} tooltip="Search everything (Ctrl K)">
                    <Search />
                    <span>Search</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem className="min-w-0 flex-1">
                <SidebarMenuButton asChild tooltip="Help and docs">
                  <a href={`${SITE_URL}/docs`} target="_blank" rel="noreferrer">
                    <CircleHelp />
                    <span>Help</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <UserMenu user={user} />
      </SidebarFooter>
    </Sidebar>
  )
}
